import { useContext, useState } from 'react';

import ArtlistDataContext from '@/context/ArtlistDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import autoCropImage from '@/pages/artwork/artwork-header/options/download-template/components/AutoCrop';
import UserService from '@/services/user.service';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import AddIcon from '@/shared-components/icons/components/actions/AddIcon';
import PublicSuccessCheck from '@/shared-components/icons/components/public/PublicSuccessCheck';
import AddArtworkToArtlistLoader from '@/shared-components/loader/special-loaders/AddArtworkToArtlistLoader';
import Text from '@/shared-components/text/components/Text';

import './styles/Row.css';

export default function Row({ artwork, isInArtlist }) {
  const { artlistData, setArtlistData } = useContext(ArtlistDataContext);
  const { stateHandler, cacheHandler } = useStateHandler();
  const [loading, setLoading] = useState(false);

  async function addArtworkToArtlist() {
    setLoading(true);

    const { response, data } = await UserService.addArtworkToArtlist(artwork, artlistData._id);

    if (response.ok) {
      const newArtworks = [...artlistData.artlist_artworks, artwork];
      let artlist_image = null;

      // Check if the artwork is the first one in the list and if there's no cover image.
      if (!artlistData.artlist_image && newArtworks.length === 1) {
        const croppedBlob = await autoCropImage(artwork.artwork_media.artwork_main_picture);
        // Upload the cropped image and update the artlist cover image.
        const { data: imageData } = await UserService.uploadImage(croppedBlob);
        if (imageData && imageData.image_id) {
          artlist_image = { image_id: imageData.image_id };
        } 
      }

      const artlist = {
        ...artlistData,
        artlist_image: artlist_image || data.artlist_image,
        artlist_artworks: newArtworks,
      };

      setArtlistData(artlist);
      setLoading(false);
      stateHandler.set("temporalPopup", {
        text: (<><em>{artwork.artwork_about.artwork_title}</em> has been added to your Artlist</>)
      });
      cacheHandler.triggerAction("ADD_TO_ARTLIST");
    } else if (data.error_type === "MAX_ARTLIST_ARTWORKS") {
      alert("You've reached the limit of artworks in a single artlist.");
      setLoading(false);
    }
  }

  async function removeArtworkFromArtlist() {
    setLoading(true);

    const { response, data } = await UserService.removeArtworkFromArtlist(artwork, artlistData._id);

    if (response.ok) {
      setLoading(false);
      setArtlistData(prevValue => ({
        ...data,
        artlist_artworks: prevValue.artlist_artworks.filter(artlistArtwork => artlistArtwork._id !== artwork._id)
      }));
      stateHandler.set("temporalPopup", {
        text: (<><em>{artwork.artwork_about.artwork_title}</em> has been removed from the artlist</>),
      });
      cacheHandler.triggerAction("REMOVE_FROM_ARTIST");
    }
  }

  return (
    <div className={`artlist-add-artworks__artwork-result element-non-selectable ${isInArtlist ? "active" : ""}`}>
      <div className="artlist-add-artworks__artwork-info">
        <ArtworkImage 
          image={artwork.artwork_media.artwork_main_picture.image_id} 
          imageClassName="artlist-add-artwork__artwork-image" 
          imageTemplateClassName="artlist-add-artwork__artwork-image"
        />

        <div className="artlist-add-artworks__artwork-text-container">
          <Text className="artlist-add-artworks__artwork-title" paragraph medium>
            {artwork.artwork_about.artwork_title}
          </Text>

          <Text className="artlist-add-artworks__artwork-artist" paragraph extraSmall>
            by{" "}
            {artwork.artwork_about.artwork_artist ?
              artwork.artwork_about.artwork_artist.user_name
              :
              artwork.artwork_about.artwork_gallery_artist.artist_name
            }
          </Text>
        </div>
      </div>

      {loading ? (
        <div className="artlist-add-artworks__spinner-wrapper">
          <div className="loader-position-wrapper">
            <AddArtworkToArtlistLoader />
          </div>
        </div>
      ) : isInArtlist ? (
        <PublicSuccessCheck
          className="artlist-add-artworks__artwork-added"
          onClick={removeArtworkFromArtlist}
        /> 
      ) : (
        <AddIcon className="artlist-add-artworks__artwork-add" onClick={addArtworkToArtlist}/>
      )}
    </div>
  );
}
